import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { ArrowLeft, Loader2, UserPlus } from 'lucide-react';

// Validate Indian mobile: 10 digits starting with 6-9
const isValidPhone = (v) => /^[6-9]\d{9}$/.test(v.replace(/\s/g, ''));

export default function CreateAdminPage() {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: '',
    email: '',
    mobile: '',
    password: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState({});

  const set = (field, value) => {
    setForm(f => ({ ...f, [field]: value }));
    if (errors[field]) setErrors(e => ({ ...e, [field]: '' }));
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim())         e.name     = 'Full name is required';
    if (!form.email.trim())        e.email    = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
                                   e.email    = 'Enter a valid email address';
    if (!form.mobile.trim())       e.mobile   = 'Phone number is required';
    else if (!isValidPhone(form.mobile))
                                   e.mobile   = 'Enter a valid 10-digit mobile number (starts with 6-9)';
    if (!form.password)            e.password = 'Password is required';
    else if (form.password.length < 8)
                                   e.password = 'Password must be at least 8 characters';
    if (form.password !== form.confirmPassword)
                                   e.confirmPassword = 'Passwords do not match';
    return e;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setSaving(true);
    try {
      await api.post('/admin/admins', {
        name:     form.name.trim(),
        email:    form.email.trim().toLowerCase(),
        mobile:   form.mobile.replace(/\s/g, ''),
        password: form.password
        // no permissions — superadmin can assign them later
      });
      toast.success('Admin account created successfully!');
      navigate('/admin/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create admin');
    } finally {
      setSaving(false);
    }
  };

  const Err = ({ f }) => errors[f]
    ? <p className="text-xs text-red-500 mt-1">{errors[f]}</p>
    : null;

  const inputCls = (f) =>
    `input-field${errors[f] ? ' border-red-400 focus:ring-red-300 focus:border-red-400' : ''}`;

  return (
    <div className="max-w-lg mx-auto animate-fade-in">

      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <button
          onClick={() => navigate('/admin/dashboard')}
          className="p-2 rounded-lg hover:bg-gray-100 transition-colors text-gray-500"
        >
          <ArrowLeft size={20} />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Create Admin</h1>
          <p className="text-gray-500 text-sm">Add a new admin account to the portal</p>
        </div>
      </div>

      {/* Form Card */}
      <div className="card">
        <form onSubmit={handleSubmit} className="space-y-5" noValidate>

          {/* Full Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Full Name <span className="text-red-500">*</span>
            </label>
            <input
              id="ca-name"
              type="text"
              className={inputCls('name')}
              placeholder="e.g. Ravi Kumar"
              value={form.name}
              onChange={e => set('name', e.target.value)}
              autoComplete="off"
            />
            <Err f="name" />
          </div>

          {/* Email */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Email Address <span className="text-red-500">*</span>
            </label>
            <input
              id="ca-email"
              type="email"
              className={inputCls('email')}
              placeholder="admin@eamcet.com"
              value={form.email}
              onChange={e => set('email', e.target.value)}
              autoComplete="off"
            />
            <Err f="email" />
          </div>

          {/* Phone */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Mobile Number <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-gray-500 font-medium border-r border-gray-300 pr-2">
                +91
              </span>
              <input
                id="ca-mobile"
                type="tel"
                maxLength={10}
                className={`${inputCls('mobile')} pl-14`}
                placeholder="9876543210"
                value={form.mobile}
                onChange={e => set('mobile', e.target.value.replace(/\D/g, '').slice(0, 10))}
                autoComplete="off"
              />
            </div>
            <Err f="mobile" />
            {!errors.mobile && (
              <p className="text-xs text-gray-400 mt-1">10-digit number starting with 6, 7, 8, or 9</p>
            )}
          </div>

          {/* Password */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Password <span className="text-red-500">*</span>
            </label>
            <input
              id="ca-password"
              type="password"
              className={inputCls('password')}
              placeholder="Min. 8 characters"
              value={form.password}
              onChange={e => set('password', e.target.value)}
              autoComplete="new-password"
            />
            <Err f="password" />
          </div>

          {/* Confirm Password */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Confirm Password <span className="text-red-500">*</span>
            </label>
            <input
              id="ca-confirm"
              type="password"
              className={inputCls('confirmPassword')}
              placeholder="Re-enter password"
              value={form.confirmPassword}
              onChange={e => set('confirmPassword', e.target.value)}
              autoComplete="new-password"
            />
            <Err f="confirmPassword" />
          </div>

          {/* Info note */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 text-sm text-yellow-800">
            <strong>Note:</strong> The account will be created immediately with <em>admin</em> role
            and marked as verified. No email OTP required. The admin can log in straight away.
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={() => navigate('/admin/dashboard')}
              className="btn-secondary flex-1 justify-center"
            >
              Cancel
            </button>
            <button
              id="ca-submit"
              type="submit"
              disabled={saving}
              className="btn-primary flex-1 justify-center gap-2"
            >
              {saving
                ? <><Loader2 size={16} className="animate-spin" /> Creating...</>
                : <><UserPlus size={16} /> Create Admin</>
              }
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
